import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, BookmarkPlus, ChevronDown, ChevronUp, X, Timer, Truck, CircleDot } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { AiTextButton } from "@/components/AiTextButton";
import { ExternalWorkDialog } from "@/components/ExternalWorkDialog";
import { TyreQuickDialog } from "@/components/TyreQuickDialog";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useFormatEUR } from "@/lib/useFormatEUR";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Diensten / werkzaamheden on the job card: collapsible list of saved tasks (editable) + free line. */
export default function ServicesPanel({ card, setCard, settings, locked }) {
  const { t } = useLang();
  const fm = useFormatEUR();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [tyreOpen, setTyreOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [catalogLocal, setCatalogLocal] = useState(null);
  const catalog = catalogLocal ?? settings?.service_catalog ?? [];
  const services = card.services || [];
  const { data: lt } = useQuery({ queryKey: ["labor-times"], queryFn: () => api.get("/labor-times").then((r) => r.data) });
  const [ltOpen, setLtOpen] = useState(false);
  const [ltFilter, setLtFilter] = useState("");
  const isTruck = card.vehicle_type === "truck" || card.vehicle_type === "trailer";
  const rate = isTruck ? Number(settings?.labor_rate_truck || 65) : Number(settings?.labor_rate || 45);
  const fmtH = (m) => `${Math.floor((m || 0) / 60)}:${String(Math.round((m || 0) % 60)).padStart(2, "0")}`;
  const addLaborTime = (it) => {
    const min = isTruck ? it.minutes_truck : it.minutes_car;
    if (!min) return toast.error(t("noTimeForVehicle"));
    persist([...services, { name: it.name, quantity: 1, unit_price: Math.round(min / 60 * rate * 100) / 100, labor_time_id: it.id, std_minutes: min }]);
    setLtOpen(false);
  };
  const stdTotal = services.reduce((s, x) => s + Number(x.std_minutes || 0) * Number(x.quantity || 1), 0);
  const shownLt = (lt?.items || []).filter((i) => !ltFilter || i.name.toLowerCase().includes(ltFilter.toLowerCase()) || i.code.toLowerCase().includes(ltFilter.toLowerCase()));

  const persist = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/repairs/${card.id}`, { services: next });
      setCard(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const add = () => {
    if (!name.trim()) return toast.error(t("serviceNameRequired"));
    persist([...services, { name: name.trim(), quantity: Number(qty) || 1, unit_price: Number(price) || 0 }]);
    setName(""); setPrice(""); setQty(1);
  };
  const pick = (s) => { setName(s.name); setPrice(s.price); setOpen(false); };
  const saveToCatalog = async () => {
    if (!name.trim()) return toast.error(t("serviceNameRequired"));
    try {
      const { data } = await api.post("/service-catalog", { name: name.trim(), price: Number(price) || 0 });
      setCatalogLocal(data.catalog); qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(data.duplicate ? t("serviceAlreadyInCatalog") : t("serviceSavedToCatalog"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeFromCatalog = async (id) => {
    try {
      const { data } = await api.delete(`/service-catalog/${id}`);
      setCatalogLocal(data.catalog); qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const shown = catalog.filter((c) => !filter || c.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Card className="p-5 space-y-4" data-testid="services-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("services")}</div>
      </div>
      {services.length > 0 && (
        <div className="space-y-2">
          {services.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 rounded-md bg-muted/40 border border-border" data-testid={`service-row-${s.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{s.quantity} × {fm(s.unit_price)}{s.external ? <span className="ml-2 text-primary" data-testid={`service-ext-${s.id}`}><Truck className="inline h-3 w-3 mr-0.5" />{t("externalWork")}</span> : null}{s.std_minutes ? <span className="ml-2 text-primary" data-testid={`service-std-${s.id}`}><Timer className="inline h-3 w-3 mr-0.5" />{t("stdTime")} {fmtH(s.std_minutes * s.quantity)}</span> : null}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm font-mono font-bold tabular-nums">{fm(s.total)}</div>
                {!locked && (
                  <button onClick={() => persist(services.filter((x) => x.id !== s.id))} disabled={busy} className="text-muted-foreground hover:text-destructive" data-testid={`service-remove-${s.id}`} aria-label={t("delete")}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {!locked && (
        <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/[0.03] p-3" data-testid="labor-time-picker">
          <button type="button" onClick={() => setLtOpen((v) => !v)} className="w-full flex items-center justify-between text-xs font-medium text-primary" data-testid="labor-time-toggle">
            <span className="flex items-center gap-1"><Timer className="h-3.5 w-3.5" />{t("pickFromLaborTimes")} ({lt?.items?.length || 0}) · {fm(rate)}/h</span>{ltOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {ltOpen && (
            <div className="rounded-md border border-border bg-background" data-testid="labor-time-list">
              <div className="p-2 border-b border-border"><Input autoFocus value={ltFilter} onChange={(e) => setLtFilter(e.target.value)} placeholder={t("search")} className="h-8" data-testid="labor-time-filter" /></div>
              <div className="max-h-64 overflow-auto">
                {shownLt.map((it) => { const min = isTruck ? it.minutes_truck : it.minutes_car; return (
                  <button key={it.id} type="button" onClick={() => addLaborTime(it)} className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-muted/50 text-sm text-left" data-testid={`labor-time-pick-${it.code}`}>
                    <span className="truncate">{it.name}</span><span className="font-mono text-xs text-muted-foreground whitespace-nowrap ml-2">{min ? `${fmtH(min)} · ${fm(min / 60 * rate)}` : "—"}</span>
                  </button>); })}
              </div>
            </div>
          )}
        </div>
      )}
      {!locked && (
        <div className="space-y-2 rounded-lg border border-dashed border-border p-3">
          <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-xs font-medium text-primary" data-testid="service-catalog-toggle">
            <span>{t("pickFromServiceCatalog")} ({catalog.length})</span>{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {open && (
            <div className="rounded-md border border-border bg-background" data-testid="service-catalog-list">
              <div className="p-2 border-b border-border"><Input autoFocus value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("search")} className="h-8" data-testid="service-catalog-filter" /></div>
              <div className="max-h-56 overflow-auto">
                {shown.length === 0 && <div className="p-3 text-xs text-muted-foreground">{t("noServicesInCatalog")}</div>}
                {shown.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 text-sm">
                    <button type="button" className="flex-1 text-left truncate" onClick={() => pick(c)} data-testid={`service-catalog-pick-${c.id}`}>{c.name}<span className="ml-2 font-mono text-xs text-muted-foreground">{fm(c.price)}</span></button>
                    <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => removeFromCatalog(c.id)} aria-label={t("delete")} data-testid={`service-catalog-remove-${c.id}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <Input className="md:col-span-2" placeholder={t("serviceName")} value={name} onChange={(e) => setName(e.target.value)} list="service-catalog-datalist" data-testid="service-name-input" />
            <div className="flex items-center"><AiTextButton value={name} onApply={setName} field="service" context={{ vehicle: [card.car_make, card.car_model].filter(Boolean).join(" ") }} testId="ai-service" /></div>
            <datalist id="service-catalog-datalist">{catalog.map((c) => <option key={c.id} value={c.name} />)}</datalist>
            <Input type="number" min="1" step="1" placeholder={t("qty")} value={qty} onChange={(e) => setQty(e.target.value)} dir="ltr" data-testid="service-qty-input" />
            <Input type="number" min="0" step="0.01" placeholder={t("price")} value={price} onChange={(e) => setPrice(e.target.value)} dir="ltr" data-testid="service-price-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={add} disabled={busy} variant="outline" className="rounded-full" data-testid="service-add-btn">
              <Plus className="h-4 w-4 mr-2" /> {t("addService")}
            </Button>
            <Button onClick={() => setExtOpen(true)} disabled={busy} variant="outline" className="rounded-full border-primary/40 text-primary" data-testid="service-external-btn">
              <Truck className="h-4 w-4 mr-2" /> {t("externalWork")}
            </Button>
            <ExternalWorkDialog open={extOpen} onOpenChange={setExtOpen} settings={settings} onAdd={(line) => persist([...services, line])} />
            <Button onClick={() => setTyreOpen(true)} disabled={busy} variant="outline" className="rounded-full border-primary/40 text-primary" data-testid="service-tyre-btn">
              <CircleDot className="h-4 w-4 mr-2" /> {t("tyreService")}
            </Button>
            {tyreOpen && <TyreQuickDialog open={tyreOpen} onOpenChange={setTyreOpen} catalog={catalog} isTruck={isTruck} onAdd={(lines) => persist([...services, ...lines])} />}
            <Button onClick={saveToCatalog} disabled={busy || !name.trim()} variant="ghost" className="rounded-full text-primary" data-testid="service-save-catalog-btn">
              <BookmarkPlus className="h-4 w-4 mr-2" /> {t("saveToServiceCatalog")}
            </Button>
          </div>
        </div>
      )}
      <div className="flex justify-between text-sm font-mono">
        <span className="text-muted-foreground">{t("servicesTotal")}{stdTotal > 0 && <span className="ml-2 text-primary" data-testid="services-std-total">· {t("stdTime")} {fmtH(stdTotal)}{card.labor_minutes > 0 && ` · ${t("actualTime")} ${fmtH(card.labor_minutes)}`}</span>}</span>
        <span className="font-bold tabular-nums" data-testid="services-total">{fm(card.services_total || 0)}</span>
      </div>
    </Card>
  );
}
